import React, { useEffect, useMemo, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { css, keyframes, useTheme } from '@emotion/react';
import { mq } from '@hangar31/rc-layout';
import Icon from '@OnArk/SVGs/Icon';
import posthog from '@OnArk/utils/posthog';

import MicroTour from './MicroTour';
import { getStepHref, StepKey, steps } from './steps';
import { tours } from './tours';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUpSheet = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MANUAL_KEY = 'onark-onboarding-manual-completed';

// Per-step "what to do on this page" copy. Keeps the contextual callout
// focused on what the user is about to do (the step's blurb is more "what is
// this step", which is fine for the directory page but too generic here).
const HOW_TO: Partial<Record<StepKey, string[]>> = {
  automations: [
    'Find the section you want to react to — usually a check-in or sign-up Form.',
    'Click “Add Automation” underneath it.',
    'Pick an action. “Send Email” always works; connect PCO first to unlock “Set PCO Field” and “Trigger PCO Workflow”.',
    'Optional: add a condition so the rule only fires for certain answers (e.g. only people who picked Membership).',
    'Save. Stack as many automations on the same section as you need.',
  ],
  billing: [
    'Pick the plan that fits your church size — billing is metered by check-ins, so you only pay for what gets used.',
    'You can change plans anytime as you grow.',
  ],
  branding: [
    'Upload your logo — guests see it at the top of every class.',
    'Upload your icon — it shows up in the browser tab and PCO sync indicator.',
    'Pick your banner and button colors. Guests see these the moment they land on a class URL.',
  ],
  course: [
    'Pick a starter template (Starting Point, Membership, Spiritual Gifts) or hit “Start from scratch”.',
    'Rename, reorder, and tweak anything later — nothing goes live until you publish.',
  ],
  guests: [
    'Every guest who checks in shows up here, with timestamp and class.',
    'Search by name or email; filter by class, completion status, and date range.',
    'Click a row to open that guest’s full detail page — every check-in, every form answer, every automation that fired for them.',
  ],
  ministries: [
    'Add a ministry for each team a guest might be routed to.',
    'Link each ministry to test result types (e.g. Spiritual Gifts → Worship Team) and OnArk auto-routes guests when they finish the test.',
  ],
  pco: [
    'Click “Connect PCO” and authorize with your Planning Center account.',
    'Once connected, automations can read or write PCO fields and trigger PCO workflows for every guest.',
  ],
  preview: [
    'Walk through the class on a phone like a real guest would.',
    'Watch for: confusing copy, mobile layout issues, and whether each automation fires when expected.',
  ],
  profile: [
    'Confirm your church name and timezone.',
    'Set the public URL slug — it ends up in every link you’ll ever share, so lock it in before you publish.',
  ],
  publish: [
    'Open your class. The Publish button lives in the top-right toolbar.',
    'A yellow dot or “Unpublished Changes” label means you have draft edits that aren’t live yet.',
    'Click Publish to push the current draft to your guests.',
    'You can keep editing — new changes stay in draft until your next publish, so guests-in-progress aren’t disturbed.',
  ],
  reports: [
    'Pick the time range up top (default: all time).',
    'The trend chart shows check-ins per day. Hover any point for the exact number.',
    'Below it, the per-class and per-automation breakdowns. Hit Download on any card to export the rows as CSV — perfect for staff meetings.',
  ],
  sections: [
    'The left sidebar lists every section in your class. Click any to edit.',
    'Add new ones with the + button; drag to reorder.',
    'Seven section types: Check-in (guest login), Form, Text, Video, Test, Branch, Completion. Each has its own auto-fired mini-tour when you first open it.',
  ],
  share: [
    'On your class page, hit “Copy Link” in the top-right toolbar.',
    'Drop the link in a newsletter, sign-up form, text blast, or QR code.',
    'Still nervous? Click “Preview” to walk it once yourself before sending.',
  ],
  team: [
    'Hit “Invite Member” and enter their email.',
    'They join as an Editor (build and edit classes). “Make owner” hands someone full access later.',
  ],
};

interface CachedCounts {
  firstCourseId?: string | null;
  firstCourseUrl?: string | null;
  churchUrl?: string | null;
}

const Callout: React.FC = () => {
  const theme = useTheme();
  const router = useRouter();
  const [closed, setClosed] = useState(false);
  const [ctx, setCtx] = useState<CachedCounts>({});
  const [tourActive, setTourActive] = useState(false);

  // Reset closed state when the step changes via query. The spotlight tour
  // no longer auto-launches — it was too intrusive. The callout opens as a
  // summary (Why + numbered how-to) and the spotlight is opt-in via the
  // "Show me on the page" button. So just clear any running tour when the
  // step changes.
  useEffect(() => {
    setClosed(false);
    setTourActive(false);
  }, [router.query.onboarding]);

  const markTourSeen = React.useCallback(() => {
    const key = router.query.onboarding as string | undefined;

    if (!key || typeof window === 'undefined') return;
    const seenKey = 'onark-onboarding-tour-seen';
    let seen: Record<string, boolean> = {};

    try {
      seen = JSON.parse(sessionStorage.getItem(seenKey) || '{}') as Record<
        string,
        boolean
      >;
    } catch {
      // ignore
    }
    seen[key] = true;
    sessionStorage.setItem(seenKey, JSON.stringify(seen));
  }, [router.query.onboarding]);

  // Hydrate URL context from the cache the Widget populates. Avoids a second
  // GraphQL request on every page load.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('onark-onboarding-counts');

      if (raw) setCtx(JSON.parse(raw) as CachedCounts);
    } catch {
      // ignore
    }
  }, []);

  const stepKey = router.query.onboarding as StepKey | undefined;
  const step = useMemo(
    () => (stepKey ? steps.find(s => s.key === stepKey) : null),
    [stepKey]
  );

  if (!step || closed) return null;
  // We deliberately do NOT suppress the callout when the step is already
  // detected as done — if the user explicitly clicked through with
  // `?onboarding=<step>`, they want the guidance even if we'd otherwise
  // count this step as complete.

  const stepIdx = steps.findIndex(s => s.key === step.key);
  const next = steps[stepIdx + 1];

  const buildHref = (k: StepKey) =>
    `${getStepHref(k, {
      churchUrl: ctx.churchUrl,
      firstCourseId: ctx.firstCourseId,
      firstCourseUrl: ctx.firstCourseUrl,
    })}?onboarding=${k}`;

  const markDone = () => {
    try {
      const current = JSON.parse(
        localStorage.getItem(MANUAL_KEY) || '{}'
      ) as Record<string, boolean>;

      current[step.key] = true;
      localStorage.setItem(MANUAL_KEY, JSON.stringify(current));
      posthog?.capture('onboardingStepToggled', {
        done: true,
        key: step.key,
        source: 'callout',
      });
    } catch {
      // ignore
    }
    // Move on to the next step if there is one.
    if (next) void router.push(buildHref(next.key));
    else setClosed(true);
  };

  const dismiss = () => {
    setClosed(true);
    // Drop the onboarding param so refreshing doesn't bring the callout back.
    // (copy + delete instead of a rest-destructure so no unused binding is left)
    const rest = { ...router.query };

    delete rest.onboarding;

    void router.replace({ pathname: router.pathname, query: rest }, undefined, {
      scroll: false,
      shallow: true,
    });
    posthog?.capture('onboardingCalloutDismissed', { key: step.key });
  };

  const howTo = HOW_TO[step.key] ?? [step.blurb];
  const stepTour = tours[step.key];

  // While a spotlight tour is running, hide the callout itself so it doesn't
  // sit on top of the tour popover.
  if (tourActive && stepTour)
    return (
      <MicroTour
        steps={stepTour}
        onClose={() => {
          markTourSeen();
          setTourActive(false);
        }}
      />
    );

  return (
    <>
      {/* Mobile-only backdrop scrim so the bottom sheet feels modal. Tap to
          dismiss. Hidden via media query on desktop. */}
      <div
        aria-hidden
        css={mq({
          animation: `${fadeIn} 0.2s ease`,
          background: 'rgba(15, 11, 26, 0.4)',
          bottom: 0,
          display: ['block', undefined, 'none'],
          left: 0,
          position: 'fixed',
          right: 0,
          top: 0,
          zIndex: 4,
        })}
        onClick={dismiss}
      />
      <aside
        aria-label={`Getting started: ${step.title}`}
        css={mq({
          animation: [
            `${slideUpSheet} 0.25s ease`,
            undefined,
            `${slideDown} 0.2s ease`,
          ],
          background: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.clickableLight}`,
          // Mobile: full-width bottom sheet anchored to the bottom of the
          // viewport, rounded only on top. Desktop: floating corner card.
          borderRadius: [
            `${theme.radius.md} ${theme.radius.md} 0 0`,
            undefined,
            theme.radius.md,
          ],
          bottom: [0, undefined, '17rem'],
          boxShadow: theme.shadows.lg,
          left: [0, undefined, 'auto'],
          maxHeight: ['80vh', undefined, '70vh'],
          maxWidth: ['100vw', undefined, '36rem'],
          overflowY: 'auto',
          padding: ['1.6rem 1.2rem 2rem', undefined, '1.4rem'],
          position: 'fixed',
          right: [0, undefined, '1.6rem'],
          width: ['100vw', undefined, '36rem'],
          zIndex: 5,
        })}
        role="region"
      >
        {/* Mobile-only drag handle */}
        <div
          aria-hidden
          css={mq({
            background: theme.colors.grayLight,
            borderRadius: '999px',
            display: ['block', undefined, 'none'],
            height: '0.4rem',
            margin: '-0.6rem auto 1rem',
            width: '4rem',
          })}
        />
        <div
          css={mq({
            display: 'grid',
            gap: '0.6rem',
            gridTemplateColumns: ['1fr auto', undefined, 'auto 1fr auto'],
          })}
        >
          <span
            aria-hidden
            css={mq({
              alignItems: 'center',
              background: theme.colors.clickable,
              borderRadius: '50%',
              color: theme.colors.white,
              display: ['none', undefined, 'inline-flex'],
              flexShrink: 0,
              fontSize: '1.4rem',
              fontWeight: 600,
              height: '3.2rem',
              justifyContent: 'center',
              width: '3.2rem',
            })}
          >
            {stepIdx + 1}
          </span>
          <div>
            <div
              css={{
                color: theme.colors.clickable,
                fontSize: '1.1rem',
                fontWeight: 600,
                letterSpacing: '1px',
                marginBottom: '0.2rem',
                textTransform: 'uppercase',
              }}
            >
              Getting started · Step {stepIdx + 1} of {steps.length}
            </div>
            <h2
              css={{
                color: theme.colors.black,
                fontSize: '1.6rem',
                fontWeight: 500,
                margin: '0 0 0.6rem',
              }}
            >
              {step.title}
            </h2>
            <ol
              css={css`
                display: grid;
                gap: 0.4rem;
                margin: 0 0 0.8rem;
                padding-left: 1.8rem;
                color: ${theme.colors.blackLight};
                font-size: 1.3rem;
                line-height: 1.45;
              `}
            >
              {howTo.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ol>
            <p
              css={{
                color: theme.colors.grayDark,
                fontSize: '1.2rem',
                fontStyle: 'italic',
                lineHeight: 1.4,
                margin: 0,
              }}
            >
              <strong
                css={{ color: theme.colors.clickable, fontStyle: 'normal' }}
              >
                Why:{' '}
              </strong>
              {step.why}
            </p>
            <div
              css={mq({
                display: 'flex',
                flexDirection: ['column', undefined, 'row'],
                flexWrap: 'wrap',
                gap: '0.6rem',
                marginTop: '1rem',
              })}
            >
              <button
                css={{
                  background: theme.colors.clickable,
                  border: 0,
                  borderRadius: theme.radius.md,
                  color: theme.colors.white,
                  cursor: 'pointer',
                  fontSize: '1.3rem',
                  fontWeight: 500,
                  padding: '0.6rem 1.2rem',
                }}
                type="button"
                onClick={markDone}
              >
                {next ? "I'm done — show me the next step" : "I'm done"}
              </button>
              {tours[step.key] && (
                <button
                  css={mq({
                    background: 'transparent',
                    border: `1px solid ${theme.colors.clickable}`,
                    borderRadius: theme.radius.md,
                    color: theme.colors.clickable,
                    cursor: 'pointer',
                    // Spotlights are desktop-only — on a narrow viewport the
                    // popover anchoring fights with the bottom sheet that's
                    // already on screen.
                    display: ['none', undefined, 'inline-flex'],
                    fontSize: '1.3rem',
                    fontWeight: 500,
                    padding: '0.6rem 1.2rem',
                  })}
                  type="button"
                  onClick={() => {
                    setTourActive(true);
                    posthog?.capture('microTourStarted', { key: step.key });
                  }}
                >
                  Show me on the page
                </button>
              )}
              <Link
                css={{
                  alignItems: 'center',
                  color: theme.colors.clickable,
                  display: 'inline-flex',
                  fontSize: '1.3rem',
                  gap: '0.4rem',
                  padding: '0.6rem 0.8rem',
                  textDecoration: 'none',
                }}
                href="/admin/getting-started"
              >
                See the full walkthrough →
              </Link>
            </div>
          </div>
          <button
            aria-label="Close"
            css={mq({
              '&:hover': { background: theme.colors.white },
              alignItems: 'center',
              alignSelf: ['start', undefined, 'start'],
              background: 'transparent',
              border: 0,
              borderRadius: '50%',
              color: theme.colors.grayDark,
              cursor: 'pointer',
              display: 'inline-flex',
              height: '2.4rem',
              justifyContent: 'center',
              width: '2.4rem',
            })}
            type="button"
            onClick={dismiss}
          >
            <Icon name="ACTIONS_Close" size="12" />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Callout;
